if (!customElements.get('collection-custom-showcase')) {
  customElements.define(
    'collection-custom-showcase',
    class CollectionCustomShowcase extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          images: ['.ccs__images'],
          tabs: ['.ccs__link'],
          collectionButton: '.ccs__collection-button',
        };

        this.elements = FoxTheme.utils.queryDomNodes(this.selectors, this);
        this.section = this.closest('.section--collection-custom-showcase');
        this.mediaQuery = window.matchMedia('(min-width: 768px)');
        this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.hoverTracker = null;
        this.autoplayTimer = null;
        this.onAutoplayChange = this.handleAutoplayChange.bind(this);
      }

      connectedCallback() {
        if (this.elements.tabs) {
          this.setActiveTab(0);

          if ('ontouchstart' in window) {
            this.elements.tabs.forEach((item) => {
              item.addEventListener('touchstart', this.onTouchChange.bind(this));
              item.addEventListener('click', this.onClick.bind(this));
            });
          } else {
            this.elements.tabs.forEach((item) => {
              item.addEventListener('mouseover', (e) => this.onMouseOver(e));
              item.addEventListener('focus', (e) => this.onMouseOver(e));
            });
          }

          this.addEventListener('mouseenter', () => this.stopAutoplay());
          this.addEventListener('mouseleave', () => this.startAutoplay());
          this.addEventListener('focusin', () => this.stopAutoplay());
          this.addEventListener('focusout', () => this.startAutoplay());

          this.addMediaQueryListener(this.mediaQuery);
          this.addMediaQueryListener(this.prefersReducedMotion);
          this.startAutoplay();
        }

        if (Shopify.designMode) {
          document.addEventListener('shopify:block:select', (e) => {
            if (e.detail.sectionId != this.dataset.sectionId) return;
            let { target } = e;
            const index = Number(target.dataset.index);
            this.setActiveTab(index);
          });
        }
      }

      disconnectedCallback() {
        this.stopAutoplay();
        this.removeMediaQueryListener(this.mediaQuery);
        this.removeMediaQueryListener(this.prefersReducedMotion);
      }

      isSelected(tab) {
        return tab.getAttribute('aria-selected') === 'true';
      }

      setActiveTab(newIndex) {
        const newTab = this.elements.tabs[newIndex];
        const newMedia = this.elements.images[newIndex];

        if (!newTab || !newMedia) return;

        this.elements.tabs.forEach((tab) => tab.setAttribute('aria-selected', false));

        this.elements.images.forEach((tabImage) => {
          tabImage.classList.remove('active');
          tabImage.hidden = true;
        });

        newTab.setAttribute('aria-selected', true);

        newMedia.classList.add('active');
        newMedia.hidden = false;
        this.setColors(newTab);
        this.setCollectionButton(newTab);
      }

      setColors(tab) {
        if (!this.section || !tab) return;

        this.section.style.setProperty('--ccs-background-color', tab.dataset.backgroundColor);
        this.section.style.setProperty('--ccs-text-color', tab.dataset.textColor);
        this.section.style.setProperty('--ccs-tab-active-color', tab.dataset.activeTabTextColor);
        this.section.style.setProperty('--ccs-tab-inactive-color', tab.dataset.inactiveTabTextColor);
      }

      setCollectionButton(tab) {
        const button = this.elements.collectionButton;

        if (!button || !tab) return;

        const collectionUrl = tab.dataset.collectionUrl;
        const collectionTitle = tab.dataset.collectionTitle;

        if (!collectionUrl) {
          button.hidden = true;
          button.removeAttribute('href');
          button.removeAttribute('aria-label');
          return;
        }

        button.hidden = false;
        button.href = collectionUrl;

        if (collectionTitle) {
          const label = button.dataset.defaultLabel || button.textContent.trim();
          button.setAttribute('aria-label', `${label}: ${collectionTitle}`);
        }
      }

      getActiveIndex() {
        return Array.from(this.elements.tabs).findIndex((tab) => this.isSelected(tab));
      }

      showNextTab() {
        const activeIndex = this.getActiveIndex();
        const nextIndex = activeIndex >= 0 ? (activeIndex + 1) % this.elements.tabs.length : 0;

        this.setActiveTab(nextIndex);
      }

      isAutoplayEnabled() {
        if (this.prefersReducedMotion.matches) return false;

        const enabled = this.mediaQuery.matches ? this.dataset.autoplayDesktop : this.dataset.autoplayMobile;

        return enabled === 'true';
      }

      startAutoplay() {
        this.stopAutoplay();

        if (!this.isAutoplayEnabled() || this.elements.tabs.length < 2) return;

        const speed = Number(this.dataset.autoplaySpeed) || 5;
        this.autoplayTimer = setInterval(() => this.showNextTab(), speed * 1000);
      }

      stopAutoplay() {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }

      handleAutoplayChange() {
        this.startAutoplay();
      }

      addMediaQueryListener(mediaQuery) {
        if (mediaQuery.addEventListener) {
          mediaQuery.addEventListener('change', this.onAutoplayChange);
        } else {
          mediaQuery.addListener(this.onAutoplayChange);
        }
      }

      removeMediaQueryListener(mediaQuery) {
        if (mediaQuery.removeEventListener) {
          mediaQuery.removeEventListener('change', this.onAutoplayChange);
        } else {
          mediaQuery.removeListener(this.onAutoplayChange);
        }
      }

      onMouseOver(e) {
        const target = e.currentTarget;
        const index = Number(target.dataset.index);

        clearTimeout(this.hoverTracker);
        this.hoverTracker = setTimeout(() => {
          if (this.isSelected(target)) return;

          this.setActiveTab(index);
          this.startAutoplay();
        }, 100);
      }

      onTouchChange(e) {
        const target = e.currentTarget;
        const index = Number(target.dataset.index);

        if (this.isSelected(target)) {
          this.preventClick = false;
          return;
        } else {
          this.preventClick = true;
        }

        this.setActiveTab(index);
        this.startAutoplay();
      }

      onClick(e) {
        if (this.preventClick) {
          e.preventDefault();
        }
      }
    }
  );
}
